/**
 * WebEnrichmentDialog — confirm dialog before web search enrichment.
 *
 * A13-N11. Stub for v1 — full Serper integration deferred.
 *
 * Props:
 *  - noteId: UUID string
 *  - open: boolean
 *  - onClose: () => void
 *  - onComplete: () => void
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../lib/apiClient';

export default function WebEnrichmentDialog({ noteId, open, onClose, onComplete }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  async function handleConfirm() {
    setIsLoading(true);
    setError(null);
    try {
      await apiClient.post(`/api/v1/aif/brain-dump/notes/${noteId}/enrich`, {});
      if (onComplete) onComplete();
      onClose();
    } catch (err) {
      setError(err.message || t('brainDump.enrichFailed', 'Anreicherung fehlgeschlagen'));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="enrich-dialog"
    >
      <div className="bg-[var(--aif-bg-page)] rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-lg font-bold text-[var(--aif-text-primary)] mb-3">
          {t('brainDump.enrichTitle', 'Web-Info finden')}
        </h2>
        <p className="text-sm text-[var(--aif-text-secondary)] mb-4">
          {t(
            'brainDump.enrichDescription',
            'Das System sucht öffentlich verfügbare Informationen passend zu deiner Notiz (z.B. Maschinen-Specs, Pflanzenschutz-Empfehlungen).'
          )}
        </p>
        <p className="text-xs text-[var(--aif-text-muted)] mb-4">
          {t('brainDump.enrichPrivacy', 'Personenbezogene Daten und private Begriffe werden NICHT abgefragt.')}
        </p>

        {error && (
          <div className="mb-3 text-sm text-[var(--aif-color-danger)] bg-[var(--aif-color-danger-light)] px-3 py-2 rounded">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-surface)] rounded transition min-h-[44px]"
          >
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className="px-4 py-2 bg-[var(--aif-color-primary)] hover:opacity-90 text-white rounded transition disabled:opacity-50 min-h-[44px]"
            data-testid="enrich-confirm"
          >
            {isLoading ? t('common.loading', 'Lädt...') : t('brainDump.enrichAction', 'Anreichern')}
          </button>
        </div>
      </div>
    </div>
  );
}
